/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { organizationRemoteSessionClientsRemoveFromMcpServer } from "../funcs/organizationRemoteSessionClientsRemoveFromMcpServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RemoveOrganizationRemoteSessionClientFromMcpServerRequest,
  RemoveOrganizationRemoteSessionClientFromMcpServerSecurity,
} from "../models/operations/removeorganizationremotesessionclientfrommcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoveOrganizationRemoteSessionClientFromMcpServerMutationVariables =
  {
    request: RemoveOrganizationRemoteSessionClientFromMcpServerRequest;
    security?:
      | RemoveOrganizationRemoteSessionClientFromMcpServerSecurity
      | undefined;
    options?: RequestOptions;
  };

export type RemoveOrganizationRemoteSessionClientFromMcpServerMutationData =
  void;

export type RemoveOrganizationRemoteSessionClientFromMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * removeClientFromMcpServer organizationRemoteSessionClients
 *
 * @remarks
 * Detach a remote_session_client from an MCP server (clears the MCP server's user_session_issuer link) in the caller's organization. Requires org:admin.
 */
export function useRemoveOrganizationRemoteSessionClientFromMcpServerMutation(
  options?: MutationHookOptions<
    RemoveOrganizationRemoteSessionClientFromMcpServerMutationData,
    RemoveOrganizationRemoteSessionClientFromMcpServerMutationError,
    RemoveOrganizationRemoteSessionClientFromMcpServerMutationVariables
  >,
): UseMutationResult<
  RemoveOrganizationRemoteSessionClientFromMcpServerMutationData,
  RemoveOrganizationRemoteSessionClientFromMcpServerMutationError,
  RemoveOrganizationRemoteSessionClientFromMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoveOrganizationRemoteSessionClientFromMcpServerMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyRemoveOrganizationRemoteSessionClientFromMcpServer(): MutationKey {
  return [
    "@gram/client",
    "organizationRemoteSessionClients",
    "removeFromMcpServer",
  ];
}

export function buildRemoveOrganizationRemoteSessionClientFromMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables:
      RemoveOrganizationRemoteSessionClientFromMcpServerMutationVariables,
  ) => Promise<RemoveOrganizationRemoteSessionClientFromMcpServerMutationData>;
} {
  return {
    mutationKey:
      mutationKeyRemoveOrganizationRemoteSessionClientFromMcpServer(),
    mutationFn:
      function removeOrganizationRemoteSessionClientFromMcpServerMutationFn({
        request,
        security,
        options,
      }): Promise<
        RemoveOrganizationRemoteSessionClientFromMcpServerMutationData
      > {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(organizationRemoteSessionClientsRemoveFromMcpServer(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
